import { defineStore } from 'pinia';
import { useRuntimeConfig } from 'nuxt/app';
import axios from 'axios';
import {
   defaultResponseStatus,
   handleApiRequest,
} from '../functions/handleApiRequest';
import type { ResponseStatus } from '../lms';
import type { SubscriptionCtaModal } from '../types/types';
import { NATIVE_CLIENT_HEADER, resolveNativeClient } from '~/functions/nativeClient';
import { requesterHeaders } from '~/functions/appOrigin';

export interface AvailablePortal {
   portal: string;
   brand_name: string;
}

// Navigation item from API nav_items tree
export interface NavItem {
   key: string;
   /**
    * 'custom_page' marks a page authored per brand in the dashboard; 'builtin' is a
    * route this app owns and maps by key. Absent on responses predating the custom
    * pages feature, so anything other than 'custom_page' is treated as built-in.
    */
   type?: 'builtin' | 'custom_page';
   /**
    * Populated for custom pages only. The API deliberately never sends a URL - this
    * app owns the path these slugs live at (see CUSTOM_PAGE_PATH_PREFIX).
    */
   slug?: string | null;
   label: string;
   position: number;
   is_visible: boolean;
   children?: NavItem[];
}

// Homepage section configuration from API
export interface HomepageSection {
   key: 'content_browser' | 'free_lesson_carousel' | 'featured_contributors' | 'featured_blog_posts' | 'leaderboard';
   label: string;
   display_order: number;
}

// Portal page content configuration from API
export interface PageContent {
   page_key: string;
   page_title?: string | null;
   show_page_title: boolean;
   title: string | null;
   body: string | null;
   panel_mode: 'default' | 'custom' | 'hidden';
   use_custom_content: boolean; // deprecated — auto-derived, do not use for rendering
}

interface UserProfileResponse {
   user: {
      name: string;
      email: string;
      title?: string;
      firstName: string;
      middleName?: string;
      lastName: string;
      phone: string;
      dateOfBirth: string;
      disability?: string;
      address1?: string;
      address2?: string;
      address3?: string;
      city?: string;
      zip?: string;
      country?: string;
      countryOfOrigin?: string;
      ethnicCode?: string;
      communicationPreference?: string;
   };
   site_settings: {
      show_logo: boolean;
      enable_user_profile_features: boolean;
      enable_self_service_subscriptions_management: boolean;
      portal: string;
      brand_name: string;
      logo_filename: string;
      show_free_content_indicators: boolean;
      has_subscription_free_access?: boolean;
      show_free_content_filter_button: boolean;
      show_play_learn_menu: boolean;
      show_all_content_menu: boolean;
      show_audience_browser: boolean;
      show_blog_menu: boolean;
      show_player_content_card: boolean;
      show_team_content_card: boolean;
      show_coach_content_card: boolean;
      show_umpire_content_card: boolean;
      show_coach_instructor_level_1_content_card: boolean;
      show_coach_instructor_level_2_content_card: boolean;
      show_levels_menu: boolean;
      show_access_options_menu: boolean;
      show_premium_packages_link: boolean;
      show_premium_subscriptions_link: boolean;
      show_site_wide_subscription_link: boolean;
      show_subscription_premium_exclusion_note: boolean;
      show_home_page_link: boolean;
      show_contributors_menu_item: boolean;
      show_free_course_carousel: boolean;
      show_featured_contributors: boolean;
      show_ipa_logo: boolean;
      show_uk_coaching_1st_4_sport_logo: boolean;
      show_aajp_logo: boolean;
      show_home_blog_posts_carousel: boolean;
      show_tournaments_link: boolean;
      show_chukkers_link: boolean;
      show_video_gallery_menu: boolean;
      show_image_gallery_menu_item: boolean;
      show_memberships_menu: boolean;
      show_leaderboard: boolean;
      nav_labels: Record<string, string>;
      nav_items?: NavItem[];
      homepage_intro_content?: string | null;
      homepage_intro_title?: string | null;
      homepage_sections?: HomepageSection[];
      page_contents?: PageContent[];
      color_scheme?: {
         button_background_color: string | null;
         main_title_color: string | null;
         footer_background_color: string | null;
      };
      updated_at?: string | number;
      admin_url?: string;
      is_first_login?: boolean;
      post_signup_redirect?: string | null;
      subscription_cta_modal?: SubscriptionCtaModal;
   };
}

/**
 * Type-safe helper to merge API site_settings with defaults
 * This ensures all feature flags default to true for backward compatibility
 * and special cases (like portal lowercase) are handled consistently
 */
const mergeSiteSettings = (
   apiResponse: Partial<UserProfileResponse['site_settings']>,
): UserProfileResponse['site_settings'] => {
   // Define feature flag defaults - these apply when API doesn't send a value
   // All feature flags default to true (show/enable by default) for backward compatibility
   const featureFlagDefaults = {
      show_free_content_filter_button: true,
      show_play_learn_menu: true,
      show_all_content_menu: true,
      show_audience_browser: true,
      show_blog_menu: true,
      show_player_content_card: true,
      show_team_content_card: true,
      show_coach_content_card: true,
      show_umpire_content_card: true,
      show_coach_instructor_level_1_content_card: true,
      show_coach_instructor_level_2_content_card: true,
      show_levels_menu: true,
      show_access_options_menu: true,
      show_premium_packages_link: true,
      show_premium_subscriptions_link: true,
      show_site_wide_subscription_link: true,
      show_subscription_premium_exclusion_note: true,
      show_home_page_link: true,
      show_contributors_menu_item: true,
      show_free_course_carousel: true,
      show_featured_contributors: true,
      show_ipa_logo: true,
      show_uk_coaching_1st_4_sport_logo: true,
      show_aajp_logo: true,
      show_tournaments_link: false,
      show_chukkers_link: false,
      show_video_gallery_menu: true,
      show_image_gallery_menu_item: false,
      show_memberships_menu: false,
      // Opt-in per brand. A brand with the feature off must not even call the endpoint.
      show_leaderboard: false,
   } satisfies Partial<UserProfileResponse['site_settings']>;

   // Merge: defaults → API response → special handling
   return {
      ...featureFlagDefaults,
      ...apiResponse,
      // Special case: portal must be lowercase, convert null to undefined
      portal: apiResponse.portal?.toLowerCase() ?? undefined,
   } as UserProfileResponse['site_settings'];
};

// Course information included in subscriptions
export interface SubscriptionCourse {
   id: number;
   title: string;
   slug: string;
   image_url: string;
   description: string;
   lessons_count: number;
   level: {
      id: number;
      title: string;
      slug: string;
   } | null;
}

// Rich subscription data returned by enhanced API
export interface UserSubscription {
   // Core plan information
   id: number;
   plan_title: string;
   billing_interval: 'monthly' | 'yearly' | 'one_time';
   price: string;
   display_price: string;
   page_slug: string;
   plan_status: 'Published' | 'Admin Preview' | 'Deprecated';
   priority_order: number;
   stripe_price_id: string;

   // Rich content (optional fields)
   description: string | null;
   short_description: string | null;
   button_text: string;
   show_best_value_badge: boolean;
   image_url: string | null;
   promo_video_url: string | null;

   // Course information
   courses: SubscriptionCourse[];
   courses_count: number;

   // Subscription metadata
   checkout_url: string;
   purchased_at: string | null;
   primary_action?: {
      action_type:
         | 'checkoutSubscription'
         | 'manageSubscription'
         | 'accessContent'
         | 'premiumSubscriptionDetails';
      slug?: string;
      access_url?: string;
   };

   // Legacy fields for backward compatibility (may be undefined)
   currency?: string;
   current_period_end?: number;
   current_period_start?: number;
   plan_id?: string;
   amount?: number;
   status?: string;
   printable_current_period_start?: string;
   printable_current_period_end?: string;
   product_name?: string;
}

export interface PurchasableSubscription {
   subscription_price: string;
   subscription_description: string;
   button_text: string;
   price_id: string;
   subscription_length_text: string;
   show_best_value_badge?: boolean;
   plan_status?: string;
   plan_type?: 'premium' | 'sitewide';
}

export interface PurchasedCourse {
   subscription_id: string;
   price_id: string;
   course_title: string;
   course_slug: string;
   course_id: number;
   amount_paid: string;
   purchased_at: string;
   access_level: string;
}

interface UserStoreState {
   lmsApiBaseurl: string;
   user: UserProfileResponse;
   accessToken: string | undefined;
   isFetchingUserProfile: boolean;
   isUpdatingUserProfile: boolean;
   isFetchingUserSubscriptions: boolean;
   currentUserSubscriptions: {
      active: UserSubscription[];
      purchased_courses: PurchasedCourse[];
      purchased_bundles: UserSubscription[];
      purchasable: PurchasableSubscription[];
   };
   //Response status codes and messages
   currentUserSubscriptionsResponseStatus: ResponseStatus;
   domain: string;
   selectedPortal: string;
   availablePortals: AvailablePortal[];
}

interface UserStoreActions {
   /**
    * @deprecated Use `useUserSubscriptions()` composable instead (TanStack Query)
    * @see composables/useUserSubscriptions.ts
    */
   setCurrentUserSubscriptions: (options?: { force?: boolean }) => Promise<void>;
   /** Resolves to the axios response on a fetch, or undefined when already loaded. */
   setCurrentUserProfile: (options?: { force?: boolean }) => Promise<unknown>;
   persistPortalSelection: (portalSlug: string) => Promise<void>;
   switchPortal: (portalSlug: string) => Promise<void>;
   updateUserProfile: (data: {
      title?: string;
      firstName: string;
      middleName?: string;
      lastName: string;
      phone: string;
      dateOfBirth: string;
      disability?: string;
      address1?: string;
      address2?: string;
      address3?: string;
      city?: string;
      zip?: string;
      country?: string;
      countryOfOrigin?: string;
      ethnicCode?: string;
      communicationPreference?: string;
   }) => Promise<
      | { success: true; data: any }
      | { success: false; errors: any; message: string }
   >;
}

interface UserStoreGetters {
   [key: string]: (state: UserStoreState) => any;

   authHeaders: (state: UserStoreState) => { [key: string]: string };
   isSubscriber: (state: UserStoreState) => boolean;
}

const useAuthStore = defineStore<
   string,
   UserStoreState,
   UserStoreGetters,
   UserStoreActions
>('auth', {
   state: (): UserStoreState => ({
      // Read straight from runtime config instead of waiting for
      // plugins/init-store-config.ts to inject it.
      //
      // Relying on that injection made every request here order-dependent, and the
      // failure was invisible: an empty base turns `${base}user-profile` into a
      // *relative* URL, which on capacitor://localhost Capacitor answers with the
      // bundled HTML shell at status 200. So `res.data` came back as an HTML string and
      // the call died on `res.data.user_profile.user` — a TypeError, with no sign that
      // an API request had gone anywhere near the wrong host. The same latent bug exists
      // on the web, where S3 serves the SPA shell for the identical reason.
      //
      // Initialising here also means $reset() restores a working base URL rather than
      // wiping it back to ''.
      lmsApiBaseurl: (useRuntimeConfig().public.lmsApiBaseurl as string) || '',
      user: {
         user: {
            name: '',
            email: '',
            title: undefined,
            firstName: '',
            middleName: undefined,
            lastName: '',
            phone: '',
            dateOfBirth: '',
            disability: undefined,
            address1: undefined,
            address2: undefined,
            address3: undefined,
            city: undefined,
            zip: undefined,
            country: undefined,
            countryOfOrigin: undefined,
            ethnicCode: undefined,
            communicationPreference: undefined,
         },
         site_settings: {
            show_logo: false,
            enable_user_profile_features: false,
            enable_self_service_subscriptions_management: false,
            portal: '',
            brand_name: '',
            logo_filename: '',
            show_free_content_indicators: false,
            show_free_content_filter_button: true,
            show_play_learn_menu: true,
            show_all_content_menu: true,
            show_audience_browser: true,
            show_blog_menu: true,
            show_player_content_card: true,
            show_team_content_card: true,
            show_coach_content_card: true,
            show_umpire_content_card: true,
            show_coach_instructor_level_1_content_card: true,
            show_coach_instructor_level_2_content_card: true,
            show_levels_menu: true,
            show_access_options_menu: true,
            show_premium_packages_link: true,
            show_premium_subscriptions_link: true,
            show_site_wide_subscription_link: true,
            show_subscription_premium_exclusion_note: true,
            show_home_page_link: true,
            show_contributors_menu_item: true,
            show_free_course_carousel: true,
            show_featured_contributors: true,
            show_ipa_logo: true,
            show_uk_coaching_1st_4_sport_logo: true,
            show_aajp_logo: true,
            show_home_blog_posts_carousel: false,
            show_tournaments_link: false,
            show_chukkers_link: false,
            show_video_gallery_menu: true,
            show_image_gallery_menu_item: false,
            show_memberships_menu: false,
            show_leaderboard: false,
            nav_labels: {},
            is_first_login: false,
            post_signup_redirect: null,
         },
      },
      accessToken: undefined,
      isFetchingUserProfile: false,
      isUpdatingUserProfile: false,
      isFetchingUserSubscriptions: false,
      currentUserSubscriptions: {
         active: [],
         purchased_courses: [],
         purchased_bundles: [],
         purchasable: [],
      },
      currentUserSubscriptionsResponseStatus: defaultResponseStatus,
      domain: '',
      // Guard: state() runs during SSR/prerender where localStorage is undefined.
      // Defaults to '' on the server; the real value is read on the client.
      selectedPortal: import.meta.client ? localStorage.getItem('selectedPortal') || '' : '',
      availablePortals: [],
   }),
   actions: {
      /**
       * The user's subscriptions, purchases and the plans they could buy.
       *
       * **Loads once and then never again, which is deliberate.** `user-subscriptions` is by
       * some distance the most expensive endpoint the API exposes — it verifies against the
       * payment provider in real time on every call, which is exactly what makes it
       * trustworthy. It must never be called on a timer or on every navigation.
       *
       * Note how weak the "already loaded" test below is: `purchasable` is populated for
       * essentially every signed-in user, so in practice this returns early on every call
       * after the first, whatever the other three arrays hold.
       *
       * Pass `{ force: true }` for the two cases where the data is known to have moved: a
       * completed checkout, and a refresh the user explicitly asked for. See
       * docs/api-reply-cache-freshness.md for why those are the only two.
       */
      async setCurrentUserSubscriptions(options: { force?: boolean } = {}) {
         // Ensure currentUserSubscriptions is always initialized as a complete object
         if (
            !this.currentUserSubscriptions ||
            !this.currentUserSubscriptions.purchased_courses
         ) {
            this.currentUserSubscriptions = {
               active: [],
               purchased_courses: [],
               purchased_bundles: [],
               purchasable: [],
            };
         }

         const isLoaded =
            this.currentUserSubscriptions.active?.length ||
            this.currentUserSubscriptions.purchased_courses?.length ||
            this.currentUserSubscriptions.purchased_bundles?.length ||
            this.currentUserSubscriptions.purchasable.length;

         if (isLoaded && !options.force) {
            return;
         }
         // Applies to forced refreshes too: a second request would race the first, and the
         // loser's response would overwrite the winner's.
         if (this.isFetchingUserSubscriptions) {
            return;
         }

         const result = await handleApiRequest<
            UserStoreState,
            {
               active: UserSubscription[];
               purchased_courses?: PurchasedCourse[];
               purchased_bundles?: UserSubscription[];
               purchasable: PurchasableSubscription[];
            }
         >(
            this,
            'isFetchingUserSubscriptions',
            'currentUserSubscriptionsResponseStatus',
            () =>
               axios.post(
                  `${this.lmsApiBaseurl}user-subscriptions`,
                  {},
                  {
                     headers: this.authHeaders,
                  },
               ),
            { ref: 'currentUserSubscriptions', value: [] },
         );

         if (result) {
            this.$patch({
               currentUserSubscriptions: {
                  active: result.active || [],
                  purchased_courses: result.purchased_courses || [],
                  purchased_bundles: result.purchased_bundles || [],
                  purchasable: result.purchasable || [],
               },
            });
         } else {
            // Ensure we always have the default empty structure
            this.$patch({
               currentUserSubscriptions: {
                  active: [],
                  purchased_courses: [],
                  purchased_bundles: [],
                  purchasable: [],
               },
            });
         }
      },
      /**
       * The user's profile and their brand's `site_settings` — the feature flags, navigation
       * and page content that decide what the whole LMS shows them.
       *
       * **Loads once per session by default.** On the web that is invisible, because a page
       * reload discards this store and the next navigation re-loads it. In the packaged app
       * there is no reload, so without `force` this value is fixed for the life of the app
       * process — which is why a user whose permissions an admin changed had to quit the app
       * entirely to see the change.
       *
       * `{ force: true }` re-fetches, and deliberately leaves the name and email currently on
       * screen in place while it does. A refresh must not blank the thing it is refreshing;
       * the first load still clears them, because there is nothing there to preserve.
       */
      async setCurrentUserProfile(options: { force?: boolean } = {}) {
         const config = useRuntimeConfig();
         const needsPortals = !config.public.singleBrand && this.availablePortals.length === 0;
         const isLoaded = Boolean(this.user.user.email) && !needsPortals;

         // Only guards the forced path. The ordinary path is guarded by its caller —
         // middleware/auth.global.ts sets `isFetchingUserProfile` to true *before* calling —
         // so testing the flag unconditionally here would make that path a permanent no-op.
         if (options.force && this.isFetchingUserProfile) {
            return;
         }

         if (isLoaded && !options.force) {
            this.isFetchingUserProfile = false;
            return;
         }
         this.isFetchingUserProfile = true;

         if (!options.force) {
            this.user.user.name = '';
            this.user.user.email = '';
         }

         try {
            const res = await axios.post(
               `${this.lmsApiBaseurl}user-profile`,
               null,
               {
                  headers: this.authHeaders,
               },
            );
            const resUserProfile = res.data.user_profile.user;
            const resUserProfileData = res.data.user_profile.profile;
            const resUserSiteSettings = res.data.user_profile.site_settings;

            this.user.user.name = resUserProfile.name;
            this.user.user.email = resUserProfile.email;
            this.user.user.title = resUserProfileData.title;
            this.user.user.firstName = resUserProfileData.firstName;
            this.user.user.middleName = resUserProfileData.middleName;
            this.user.user.lastName = resUserProfileData.lastName;
            this.user.user.phone = resUserProfileData.phone;
            this.user.user.dateOfBirth = resUserProfileData.dateOfBirth;
            this.user.user.disability = resUserProfileData.disability;
            this.user.user.address1 = resUserProfileData.address1;
            this.user.user.address2 = resUserProfileData.address2;
            this.user.user.address3 = resUserProfileData.address3;
            this.user.user.city = resUserProfileData.city;
            this.user.user.zip = resUserProfileData.zip;
            this.user.user.country = resUserProfileData.country;
            this.user.user.countryOfOrigin = resUserProfileData.countryOfOrigin;
            this.user.user.ethnicCode = resUserProfileData.ethnicCode;
            this.user.user.communicationPreference =
               resUserProfileData.communicationPreference;

            // Use type-safe helper to merge site_settings with defaults
            // This automatically handles all current and future fields
            this.user.site_settings = mergeSiteSettings(resUserSiteSettings);

            if (this.user.site_settings.portal) {
               // Only sync localStorage.portal from the API when there is no active
               // selectedPortal.  selectedPortal is set by /login/[portal] and
               // switchPortal() — it represents the user's explicit intent and must
               // not be overwritten by a stale server-side preference (the persist
               // call may not have reached the server yet).
               const activeSelection = localStorage.getItem('selectedPortal');
               if (!activeSelection) {
                  localStorage.setItem('portal', this.user.site_settings.portal);
               }
            }

            const config = useRuntimeConfig();
            if (!config.public.singleBrand && resUserSiteSettings.available_portals) {
               this.availablePortals = resUserSiteSettings.available_portals;
            }

            this.isFetchingUserProfile = false;
            return res;
         } catch (error) {
            console.error('[auth-store] setCurrentUserProfile error:', error);
            this.isFetchingUserProfile = false;
            throw error;
         }
      },
      async updateUserProfile(data) {
         this.isUpdatingUserProfile = true;
         try {
            const res = await axios.patch(
               `${this.lmsApiBaseurl}user-profile`,
               data,
               {
                  headers: this.authHeaders,
               },
            );
            this.user.user = {
               name: res.data.name,
               email: res.data.email,
               title: res.data.title,
               firstName: res.data.firstName,
               middleName: res.data.middleName,
               lastName: res.data.lastName,
               phone: res.data.phone,
               dateOfBirth: res.data.dateOfBirth,
            };
            this.isUpdatingUserProfile = false;
            return { success: true, data: res.data };
         } catch (error: any) {
            this.isUpdatingUserProfile = false;
            if (error.response?.status === 422) {
               return {
                  success: false,
                  errors: error.response.data.errors,
                  message: error.response.data.message,
               };
            }
            throw error;
         }
      },
      async switchPortal(portalSlug: string) {
         this.selectedPortal = portalSlug;
         localStorage.setItem('selectedPortal', portalSlug);

         // Persist selection to server so it survives token expiry/page reload
         await this.persistPortalSelection(portalSlug);

         // Reset profile so setCurrentUserProfile will refetch with new X-Portal header
         this.user.user.email = '';
         this.isFetchingUserProfile = false;
         await this.setCurrentUserProfile();
      },
      async persistPortalSelection(portalSlug: string) {
         try {
            await axios.post(
               `${this.lmsApiBaseurl}user-portal-selection`,
               { portal: portalSlug },
               { headers: this.authHeaders },
            );
         } catch (error) {
            console.error('[auth-store] Failed to persist portal selection:', error);
         }
      },
   },
   getters: {
      authHeaders: (state) => {
         const config = useRuntimeConfig();

         const headers: { [key: string]: string } = {
            ...requesterHeaders(config.public.siteDomain as string),
            'X-Portal': config.public.singleBrand ? config.public.portalBrand as string : (state.selectedPortal || config.public.portalBrand as string),
            'X-Single-Brand': config.public.singleBrand ? 'true' : 'false',
         };

         // Only attach the bearer token when we actually have one. Interpolating an
         // undefined accessToken produced the literal string "Bearer undefined",
         // which Auth0 rejects with a 401 ("JWT must contain two dots"). Omitting the
         // header entirely lets callers guard on its absence instead.
         if (state.accessToken) {
            headers.Authorization = `Bearer ${state.accessToken}`;
         }

         // Tells the API this request came from the packaged app, which is what makes it
         // mark Stripe's success_url so the buyer can be bounced back into the app rather
         // than left on the website. Absent on the web, where the API's behaviour is
         // unchanged. See functions/nativeClient.ts.
         const nativeClient = resolveNativeClient();

         if (nativeClient) {
            headers[NATIVE_CLIENT_HEADER] = nativeClient;
         }

         return headers;
      },
      isSubscriber: (state) => {
         return (
            state.currentUserSubscriptions?.active?.length > 0 ||
            state.currentUserSubscriptions?.purchased_courses?.length > 0 ||
            state.user?.site_settings?.portal === 'uspa'
         );
      },
   },
});

export default useAuthStore;
